import { NextGenUiToggle } from '@components/NextGenUiToggle';
import { PageNotFound } from '@components/PageNotFound';
import { useSessionDetails } from '@hooks/data/useSessionDetails';
import {
  Navigate,
  Route,
  RouteProps,
  Routes,
  useParams,
} from 'react-router-dom';
import { SessionDetailsPage } from './SessionDetailsPage';
import { OverviewTab } from '@components/tabs/overview/OverviewTab';
import { InstructorsTab } from '@components/tabs/InstructorsTab';
import { ParticipantsTab } from '@components/tabs/ParticipantsTab';
import { EventsTab } from '@components/tabs/events/EventsTab';

export type TTabKeys = 'overview' | 'participants' | 'instructors' | 'events';
type TTabRoute = RouteProps & {
  enabled?: boolean;
};

export const SessionDetailsRoute = () => {
  const { sessionId } = useParams();

  const { data: sessionDetails } = useSessionDetails({
    enabled: true,
    sessionId,
  });

  if (!sessionDetails) {
    return null;
  }

  const tabRoutes: Record<TTabKeys, TTabRoute> = {
    overview: {
      enabled: true,
      path: 'overview',
      element: <OverviewTab sessionDetails={sessionDetails} />,
    },
    instructors: {
      enabled: true,
      path: 'instructors',
      element: <InstructorsTab persons={sessionDetails.trainers} />,
    },
    participants: {
      enabled:
        sessionDetails.participantInfo?.enabled &&
        sessionDetails.participantInfo?.showThumbnail,
      path: 'participants',
      element: <ParticipantsTab persons={sessionDetails.participants} />,
    },
    events: {
      enabled: true,
      path: 'events',
      element: <EventsTab dates={sessionDetails.dates} />,
    },
  };

  const enabledTabKeys = Object.entries(tabRoutes).reduce((acc, cur) => {
    const [key, route] = cur;
    if (route.enabled) {
      return [...acc, key as TTabKeys];
    }
    return acc;
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Navigate to={enabledTabKeys[0]} replace />} />
      <Route
        path="/"
        element={
          <>
            <NextGenUiToggle isLoading={false} />
            <SessionDetailsPage
              tabKeys={enabledTabKeys}
              sessionDetails={sessionDetails}
            />
          </>
        }
      >
        {Object.values(tabRoutes)
          .filter((route) => route.enabled)
          .map((route) => (
            <Route key={route.path} {...route} />
          ))}
      </Route>
      <Route path="/*" element={<PageNotFound />} />
    </Routes>
  );
};
